import {t} from "ftc-common/i18n";
import {useMatchTimer} from "ftc-common/hooks";
import {Alert, Button, ButtonGroup, OverlayTrigger, Popover} from "react-bootstrap";
import {ReactNode, useEffect, useId} from "react";
import {messageDialog} from "first-common/dialogs";

import './ScoreCalculator.css';
import {useLocalStorageState} from "first-common/utils";
import {Temporal} from "temporal-polyfill";
import {BaseScoreType, ScoreCalculatorSeason} from "./season";
import {AllianceColor} from "ftc-common/javaTypes";

function formatTime(time: number) {
    return `${Math.floor(time / 60)}:${(time % 60).toString().padStart(2, '0')}`
}

export function ScoreCalculator<ScoreType extends BaseScoreType>({season} : {season: ScoreCalculatorSeason<ScoreType>}) {
    const [scoreData, updateScoreData] = useLocalStorageState<ScoreType>(`score-calculator-score-${season.name}`, season.defaultScore);
    const [random, setRandom] = useLocalStorageState<number>(`score-calculator-random-${season.name}`, 0);
    const [alliance, setAllianceState] = useLocalStorageState<AllianceColor>(`score-calculator-alliance-${season.name}`, AllianceColor.RED);
    const [useTimer, setUseTimer] = useLocalStorageState<boolean>(`score-calculator-use-timer`, false);
    const [startTime, setStartTime] = useLocalStorageState<number>(`score-calculator-start-time`, 0);
    const [scoreCalculatorUsed, setScoreCalculatorUsed] = useLocalStorageState<boolean>(`score-calculator-used`, false);
    const id = useId();

    function setScoreValue<K extends keyof ScoreType>(key: K, value: ScoreType[K]) {
        updateScoreData((was : ScoreType) => {
            const newScores = {...was, [key]: value}
            season.calculateScoreBreakdown(newScores, random, alliance);

            return newScores;
        })
        setScoreCalculatorUsed((was) => {
            if(!was) {
                plausible('ScoreCalculatorUsed')
            }
            return true;
        })
    }

    function setAlliance(alliance: AllianceColor) {
        setAllianceState(alliance)
        updateScoreData((was : ScoreType) => {
            const newScores = {...was}
            season.calculateScoreBreakdown(newScores, random, alliance);

            return newScores;
        })
    }

    const mute = false
    const timer = useMatchTimer({
        startAt: startTime,
        nowCallback: () => Date.now(),
        sounds: mute ? {} : (
            {
                [120 + 30 + 8]: 'matchStart',
                [120 + 8]: 'autoEnd',
                [120 + 6]: 'controllers',
                [120 + 3]: 'countdown',
                [120]: 'startTeleop',
                [60]: 'endgameWarning', // TODO replace with midmatchWarning once that exists
                [20]: 'endgameWarning',
                [0]: 'matchEnd'
            })
    }, {
        auto: Temporal.Duration.from({ seconds: 30 }),
        transition: Temporal.Duration.from({ seconds: 8 }),
        teleop: Temporal.Duration.from({ seconds: 120 }),
        midmatchWarning: Temporal.Duration.from({ seconds: 60 }),
        endgameWarning: Temporal.Duration.from({ seconds: 20 }),
        total: Temporal.Duration.from({ seconds: 30 + 8 + 120 }),
    })
    let displayTime: [ReactNode] = [t`Period Time: `]
    if(startTime != 0) {
        if (timer.auto) {
            displayTime.push(formatTime(timer.remaining - 120 - 8), ' ', <i className={`fas fa-robot`} />);
        } else if (timer.transition) {
            displayTime.push(formatTime(timer.remaining - 120), ' ', <i className={`fas fa-hand`} />);
        } else if (timer.teleop || timer.endgame) {
            displayTime.push(formatTime(timer.remaining), ' ', <i className={`fas fa-gamepad`} />);
        } else if (timer.done) {
            displayTime.push(formatTime(0));
        }
    } else {
        displayTime = [t`Timer not started`]
    }

    const scoreProps = {
        score: scoreData,
        setScoreValue
    }

    useEffect(() => {
        if(import.meta.env.DEV) {
            return;
        }
        messageDialog({
            label: t`Warning`,
            text: t`This calculator is for team practice only.<br/>Do NOT use this at events.`,
            dangerous: true
        })
    }, [])

    const errors = season.validationErrors ? season.validationErrors(scoreData) : [];

    const { ScoreBreakdown, Auto, Teleop, Penalties } = season;
    const Randomization = season.Randomization || (() => null);
    const totalScore = 'preFoulTotal' in scoreData ? scoreData.preFoulTotal : scoreData.prePenaltyTotal;

    return <>
        <div className="row justify-content-center">
            <Alert variant={"warning"} className="w-auto">
                <i className="fa fa-exclamation-triangle"></i>&nbsp;
                {t`This calculator is for team practice only.  Do NOT use this at events.`}
                &nbsp;<i className="fa fa-exclamation-triangle"></i>
            </Alert>
            <div className="col-12 text-center">
                <p className={"h2"}>{t`${season.name} Score Calculator`}</p>
            </div>
        </div>
        <div className=" row justify-content-center scoreArea">
            <div className="col-12 col-sm-10 text-center">
                <p className={"h3"}>
                    {t`Total Score: ${totalScore}`}
                    {errors.length > 0 ?
                        <OverlayTrigger trigger={["click", "hover"]} placement={"bottom"} overlay={<Popover id={`validation-errors-${id}`}>
                            <Popover.Body>
                                <ul className={"mb-0"}>
                                    {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                                </ul>
                            </Popover.Body>
                        </Popover>}>
                            <i className={"fa fa-exclamation-triangle"}></i>
                        </OverlayTrigger>
                    : null}
                </p>
            </div>
            <ScoreBreakdown score={scoreData} />
        </div>
        <div className="row justify-content-center">
            <div className={"col-12 col-sm-10 text-center"}>
                <label htmlFor={"enable-timer"}>{t`Enable Timer:`}</label> <input id="enable-timer" type={"checkbox"} checked={useTimer} onChange={(e) => setUseTimer(e.target.checked)} />
            </div>
        </div>
        {useTimer ?
            <div className="row justify-content-center">
                {timer.running ?
                    <Button variant={"danger"} onClick={() => setStartTime(0)} className={"col-11 col-sm-10"}>{t`Abort Match`}</Button> :
                    <Button variant={"success"} onClick={() => setStartTime(Date.now())} className={"col-11 col-sm-10"}>{t`Start Timer`}</Button> }

                <div className={"col-10 text-center"}>
                    {displayTime}
                </div>
            </div> : null}
        {season.allianceSpecific ? <div className="row justify-content-center mb-2">
            <div className="col-11 col-sm-10 text-center">
                Alliance:&nbsp;
                <ButtonGroup className={"me-3"}>
                    <Button variant={"outline-red"} active={alliance == AllianceColor.RED} onClick={() => setAlliance(AllianceColor.RED)}>Red</Button>
                    <Button variant={"outline-blue"} active={alliance == AllianceColor.BLUE} onClick={() => setAlliance(AllianceColor.BLUE)}>Blue</Button>
                </ButtonGroup>
            </div>
        </div> : null}
        <div className="row justify-content-center mb-2">
            <Randomization random={random} setRandom={(val) => {
                setRandom(val)
                updateScoreData((was : ScoreType) => {
                    const newScores = {...was}
                    season.calculateScoreBreakdown(newScores, val, alliance);

                    return newScores;
                })
            }}/>
        </div>
        <div className="row justify-content-center">
            <div className="col-11 col-sm-10 alert alert-dark text-center">
                AUTO Scores
            </div>
        </div>
        <Auto {...scoreProps} random={random} />
        <div className="row justify-content-center">
            <div className="col-11 col-sm-10 alert alert-dark text-center">
                TELEOP Scores
            </div>
        </div>
        <Teleop {...scoreProps} random={random} />
        <Penalties {...scoreProps} />
        <div className="row justify-content-center">
            <Button variant={"warning"} onClick={() => {
                setStartTime(0)
                setScoreCalculatorUsed(false)
                setRandom(0)
                updateScoreData(season.defaultScore)
            }} className={"col-11 col-sm-10"}>{t`Reset`}</Button>
        </div>
        {/* Since penalties are subtractive there is no good way to show them here */}
    </>;
}